import React, { useState } from "react";
import { Calendar, Tag, CheckCircle, XCircle, BookOpen } from "lucide-react";
import ActivityActions from "./ActivityActions";
import ActivityDetailModal from "./ActivityDetailModal";
import { formatDateForDisplay } from "../../../utils/dateHelpers";

/**
 * Professional activity card with compact view
 */
const ActivityCard = ({ activity, onEdit, onDelete }) => {
  const [showModal, setShowModal] = useState(false);

  const isApproved = activity.status === "APPROVED" || activity.reactions?.some((r) => r.type === "✅" || r.decision === "Approved");
  const isRejected = activity.status === "REJECTED" || activity.reactions?.some((r) => r.type === "❌" || r.decision === "Rejected");
  
  // Approved activities are locked. Rejected activities can be edited and resubmitted.
  const canEdit = !isApproved;
  const canDelete = !isApproved;

  // Extract latest reaction / comment from faculty
  const latestComment = activity.comments?.[activity.comments.length - 1];
  const latestReaction = activity.reactions?.[activity.reactions.length - 1];
  const hasReactionData = isApproved || isRejected || !!latestComment || !!latestReaction;

  const evaluatorName = latestComment?.from || latestReaction?.from || "Faculty Member";
  const ratingVal = latestComment?.assessment?.rating || latestReaction?.rating;
  const scoreVal = latestComment?.assessment?.numericScore !== undefined && latestComment?.assessment?.numericScore !== null
    ? latestComment.assessment.numericScore
    : latestReaction?.score;
  const feedbackText = latestComment?.text;

  return (
    <>
      <div
        className="
          bg-white rounded-xl shadow-sm border border-gray-200 p-6
          hover:shadow-md transition-all duration-200
        "
      >
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          {/* Activity Info */}
          <div className="flex-1 min-w-0">
            {/* Title with Status Badge */}
            <div className="flex items-start gap-3 mb-3">
              <h3 className="text-lg font-semibold text-gray-900 flex-1">
                {activity.title}
              </h3>

              {/* Status Badge */}
              {isApproved && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-50 text-green-700 text-xs font-medium rounded-full border border-green-200">
                  <CheckCircle className="w-3.5 h-3.5" />
                  Approved
                </span>
              )}
              {isRejected && !isApproved && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-50 text-red-700 text-xs font-medium rounded-full border border-red-200">
                  <XCircle className="w-3.5 h-3.5" />
                  Rejected
                </span>
              )}
              {!isApproved && !isRejected && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-50 text-amber-700 text-xs font-medium rounded-full border border-amber-200">
                  Pending Review
                </span>
              )}
            </div>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
              {/* Date */}
              {activity.date && (
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  {formatDateForDisplay(activity.date)}
                </span>
              )}

              {/* Type */}
              {activity.type && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full">
                  <Tag className="w-3.5 h-3.5" />
                  {activity.type}
                </span>
              )}

              {(activity.procedure_category || activity.procedureCategory) && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-indigo-50 text-indigo-700 text-xs font-semibold rounded-full border border-indigo-200">
                  Category: {activity.procedure_category || activity.procedureCategory}
                </span>
              )}

              {(activity.procedure_sub_type || activity.procedureSubtype) && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-purple-50 text-purple-700 text-xs font-semibold rounded-full border border-purple-200">
                  Procedure: {activity.procedure_sub_type || activity.procedureSubtype}
                </span>
              )}

              {/* Attempt Type */}
              {activity.attemptType && (
                <span
                  className={`
                    inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full
                    ${
                      activity.attemptType === "First(F)"
                        ? "bg-green-50 text-green-700"
                        : activity.attemptType === "Repeat(R)"
                          ? "bg-yellow-50 text-yellow-700"
                          : "bg-orange-50 text-orange-700"
                    }
                  `}
                >
                  {activity.attemptType}
                </span>
              )}
            </div>

            {/* Linked Competencies */}
            {activity.competencies && activity.competencies.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-100 flex flex-col gap-1.5">
                <div className="text-[11px] font-bold text-indigo-700 flex items-center gap-1 uppercase tracking-wider">
                  <BookOpen className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                  Competencies ({activity.competencies.length})
                </div>
                <div className="flex flex-col gap-1.5">
                  {activity.competencies.map((comp, idx) => (
                    <div key={idx} className="text-xs text-slate-800 bg-slate-50 border border-slate-200 rounded-lg p-2 flex items-start gap-2">
                      <span className="font-mono font-bold bg-white text-gray-900 border border-gray-300 px-1.5 py-0.5 rounded text-[10px] shrink-0 mt-0.5 shadow-xs">{comp.code}</span>
                      <span className="font-medium leading-relaxed">{comp.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Stats */}
            <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
              {activity.images?.length > 0 && (
                <span>
                  📷 {activity.images.length} image
                  {activity.images.length > 1 ? "s" : ""}
                </span>
              )}
              {activity.drawing && <span>✏️ Drawing</span>}
              {activity.reactions?.length > 0 && (
                <span>
                  💬 {activity.reactions.length} reaction
                  {activity.reactions.length > 1 ? "s" : ""}
                </span>
              )}
              {activity.comments?.length > 0 && (
                <span>
                  📝 {activity.comments.length} comment
                  {activity.comments.length > 1 ? "s" : ""}
                </span>
              )}
            </div>
          </div>

          {/* Actions */}
          <ActivityActions
            activity={activity}
            canEdit={canEdit}
            canDelete={canDelete}
            onViewDetails={() => setShowModal(true)}
            onEdit={() => typeof onEdit === "function" && onEdit(activity)}
            onDelete={() => typeof onDelete === "function" && onDelete(activity.id)}
          />
        </div>
      </div>

      {/* Detail Modal */}
      <ActivityDetailModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        activity={activity}
      />
    </>
  );
};

export default ActivityCard;

//30
